import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Select, Skeleton, Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { InvitePersonRequest, MembershipRole, PeopleAccessRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

const ROLES: MembershipRole[] = ['owner', 'principal', 'vice_principal', 'coordinator', 'teacher', 'clerk', 'support'];

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string; error?: string } | null)?.message ?? (err.body as { error?: string } | null)?.error ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Admin — People access review + invitations. Sibling of /settings/roles;
 * this view emphasises invitation flow and access review (who can sign in,
 * who's still without a password).
 */
export function SettingsPeoplePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['settings-people', institution],
    queryFn: () => productApi.listPeopleAccess(institution),
    enabled: !!institution,
  });
  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      productApi.setPersonMembershipStatus(institution, id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-people', institution] }),
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>People &amp; access</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Who has access to this institution and how. Role edits live at{' '}
            <a href={`/i/${institution}/admin/settings/roles`}>Roles &amp; access</a>.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Invite person</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card size="small"><Statistic title="Active" value={q.data.by_status.active} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Inactive" value={q.data.by_status.inactive} /></Card>
        <Card size="small"><Statistic title="No password set"
          value={q.data.without_password}
          valueStyle={{ color: q.data.without_password > 0 ? '#a2680a' : '#14213d' }} /></Card>
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="Nobody has access yet." style={{ padding: 20 }} />
          : (
            <Table<PeopleAccessRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="membership_id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Name', key: 'n',
                  render: (_, r) => <>
                    <b>{r.name}</b>
                    <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.email}</div>
                  </> },
                { title: 'Role', dataIndex: 'role', key: 'r', width: 140,
                  render: (v: MembershipRole) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
                { title: 'Password', dataIndex: 'has_password', key: 'pw', width: 130,
                  render: (v: boolean) => v ? <Tag color="success">set</Tag> : <Tag color="warning">not set</Tag> },
                { title: 'Access', dataIndex: 'status', key: 's', width: 120,
                  render: (v: 'active' | 'inactive') => <Tag color={v === 'active' ? 'success' : 'default'}>{v}</Tag> },
                { title: 'Added', dataIndex: 'created_at', key: 'c', width: 130,
                  render: (v: string) => new Date(v).toLocaleDateString() },
                { title: '', key: 'act', width: 130,
                  render: (_, r) => (
                    <Button size="small" danger={r.status === 'active'}
                      onClick={() => toggle.mutate({ id: r.membership_id, status: r.status === 'active' ? 'inactive' : 'active' })}>
                      {r.status === 'active' ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  ) },
              ]}
            />
          )}
      </Card>

      <Alert type="info" showIcon
        message="How invitations work today"
        description={<>
          Inviting a person creates their <code>person</code> row (email + name) and an active membership at this institution.
          They set a password via <code>/reset/&#123;token&#125;</code> when we hand them the reset URL. Automated invitation
          email lands with the Comms delivery pipeline.
        </>} />

      <InviteModal open={addOpen} institution={institution}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['settings-people', institution] })} />
    </Space>
  );
}

function InviteModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<InvitePersonRequest>();
  const m = useMutation({
    mutationFn: (input: InvitePersonRequest) => productApi.invitePerson(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Invited'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="Invite person" onCancel={() => { m.reset(); onClose(); }}
      okText="Invite" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ role: 'teacher' as MembershipRole }}
        onFinish={(v) => m.mutate({
          name: v.name.trim(),
          email: v.email.trim().toLowerCase(),
          role: v.role,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="name" label="Full name" rules={[{ required: true, min: 1, max: 160 }]}><Input autoFocus /></Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input placeholder="name@school.in" />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select options={ROLES.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))} />
        </Form.Item>
        <Form.Item name="note" label="Note (internal)"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
